import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
/**
 * Storage service
 * @class StorageService
 */
export class StorageService {
  /**
   * strorage lang key
   * @param {string} storageLangKey
   */
  private storageLangKey = 'mycenaLang';
  /**
   * strorage theme key
   * @param {string} storageThemeKey
   */
  private storageThemeKey = 'mycenaTheme';
  /**
   * strorage jwt key
   * @param {string} storageJwtKey
   */
  private storageJwtKey = 'mycenaToken';
  /**
   * strorage display preferences
   * @param {string} userDisplayPreferences
   */
  private userDisplayPreferences = 'mycenaDisplayPrefDeskCenter';
  /**
   * strorage selected columns 
   * @param {string}
   */
  private applicationAccessSelectedColumns = 'applicationAccessSelectedColumns';
  /**
   * strorage selected columns PU
   * @param {string}
   */
  private preloadUrlSelectedColumns = 'preloadUrlSelectedColumns';
  /**
   * strorage selected columns PP
   * @param {string}
   */
  private preloadPasswordSelectedColumns = 'preloadPasswordSelectedColumns';
  /**
   * strorage selected columns User
   * @param {string}
   */
  private userSelectedColumns = 'userSelectedColumns';
  /**
   * strorage selected columns CC
   * @param {string}
   */
  private creditCardSelectedColumns = 'creditCardSelectedColumns';
  /**
   * strorage selected columns AK
   * @param {string}
   */
  private userAccessKitSelectedColumns = 'userAccessKitSelectedColumns';
  /**
   * strorage selected columns Filter AD
   * @param {string}
   */
  private filterAdSelectedColumns = 'filterAdSelectedColumns';
  /**
   * strorage selected columns Role
   * @param {string}
   */
  private roleSelectedColumns = 'roleSelectedColumns';
  /**
   * strorage selected columns GRC
   * @param {string}
   */
  private grcSelectedColumns = 'grcSelectedColumns';
  /**
   * strorage chatbot history
   * @param {string}
   */
  private chatbotHistoryKey = 'mycena_chatbot_history';
  /**
   * strorage session uuid
   * @param {string}
   */
  private sessionUUID = 'sessionUUID';
  /**
   * strorage last url
   * @param {string}
   */
  private lastUrl = 'lastUrl';
  /**
   * strorage user region
   * @param {string}
   */
  private userRegion = 'userRegion';
  /**
  * strorage user pref
  * @param {UserPref}
  */
  public userPref: UserPref = {
    usersChart: true,
    seatsChart: true,
    passwordChart: true,
    params: true,
    preloadPassword: true,
    preloadUrl: true,
    profile: true,
    roles: true,
    uploadPreloadPassword: true,
    uploadPreloadUrl: true,
    uploadUsers: true,
    users: true,
    grcDownload: true,
    grcList: true,
    grcChart: true,
    servers: true,
    ips: true,
    serversDoc: true,
    a2f: true,
    filterAd: true,
    accessKit: true,
    uploadApplicationAccess: true,
    creditCards: true,
    uploadCreditCard: true,
    uploadUserAccessTemplate: true,
    uploadPreloadUserPassword: true,
    userPreloadAccess: true,
    reportPreloadAccess: true,
    deviceRestrictionDoc: true,
    deviceRestriction: true,
    uploadDeviceRestriction: true,
    ftpsftp: true,
    changeOwner: true,
    changeAccessKey: true
  };

  /**
   * Gets the jwt token from the session storage.
   * @returns {string} the jwt token.
   */
  getToken(): string {
    return sessionStorage.getItem(this.storageJwtKey);
  }

  /**
   * Sets the jwt token in the session storage.
   * @param token - The jwt token to be stored.
   */
  setToken(token: string): void {
    sessionStorage.setItem(this.storageJwtKey, token);
  }

  /**
   * Clears the jwt token from the session storage.
   */
  clearToken(): void {
    sessionStorage.removeItem(this.storageJwtKey);
  }

  /**
   * Retrieves the last URL stored in the session storage.
   * @returns {string} The last URL accessed by the user.
   */
  getUrl(): string {
    return sessionStorage.getItem(this.lastUrl);
  }

  /**
   * Sets the last URL accessed by the user in the session storage.
   * @param lastUrl the last URL accessed by the user.
   */
  setUrl(lastUrl: string): void {
    sessionStorage.setItem(this.lastUrl, lastUrl);
  }
  /**
   * Removes the last URL from the session storage.
   */
  clearUrl(): void {
    sessionStorage.removeItem(this.lastUrl);
  }

  /**
   * Retrieves the current language stored in the local storage.
   * @returns {string} The current language.
   */
  getLang(): string {
    return localStorage.getItem(this.storageLangKey);
  }

  /**
   * Sets the current language in the local storage.
   * @param lang The language code to be set as the current language.
   */
  setLang(lang: string): void {
    localStorage.setItem(this.storageLangKey, lang);
  }

  /**
   * Retrieves the selected columns for the Access Kit page from the local storage.
   * @returns {string} The selected columns as a stringified JSON object.
   */
  getAccessKitSelectedColumns(): string {
    return localStorage.getItem(this.applicationAccessSelectedColumns);
  }

  /**
   * Sets the selected columns for the Access Kit page in the local storage.
   * @param columns The selected columns as a JSON object.
   */
  setAccessKitSelectedColumns(columns): void {
    localStorage.setItem(this.applicationAccessSelectedColumns, JSON.stringify(columns));
  }

  /**
   * Retrieves the selected columns for the Preload URL page from the local storage.
   * @returns {string} The selected columns as a stringified JSON object.
   */
  getPreloadUrlSelectedColumns(): string {
    return localStorage.getItem(this.preloadUrlSelectedColumns);
  }

  /**
   * Sets the selected columns for the Preload URL page in the local storage.
   * @param {TableComlumns[]} columns The selected columns as a JSON object.
   */
  setPreloadUrlSelectedColumns(columns): void {
    localStorage.setItem(this.preloadUrlSelectedColumns, JSON.stringify(columns));
  }

  /**
   * Retrieves the selected columns for the Preload Password page from the local storage.
   * @returns {string} The selected columns as a stringified JSON object.
   */
  getPreloadPasswordSelectedColumns(): string {
    return localStorage.getItem(this.preloadPasswordSelectedColumns);
  }

  /**
   * Sets the selected columns for the Preload Password page in the local storage.
   * @param {TableComlumns[]} columns The selected columns as a JSON object.
   */
  setPreloadPasswordSelectedColumns(columns): void {
    localStorage.setItem(this.preloadPasswordSelectedColumns, JSON.stringify(columns));
  }

  /**
   * Retrieves the selected columns for the Users page from the local storage.
   * @returns {string} The selected columns as a stringified JSON object.
   */
  getUserSelectedColumns(): string {
    return localStorage.getItem(this.userSelectedColumns);
  }

  /**
   * Sets the selected columns for the Users page in the local storage.
   * @param {TableComlumns[]} columns The selected columns as a JSON object.
   */
  setUserSelectedColumns(columns): void {
    localStorage.setItem(this.userSelectedColumns, JSON.stringify(columns));
  }

  /**
   * Retrieves the selected columns for the Credit Card page from the local storage.
   * @returns {string} The selected columns as a stringified JSON object.
   */
  getCreditCardSelectedColumns(): string {
    return localStorage.getItem(this.creditCardSelectedColumns);
  }

  /**
   * Sets the selected columns for the Credit Card page in the local storage.
   * @param {TableComlumns[]} columns - The selected columns as a JSON object.
   */
  setCreditCardSelectedColumns(columns): void {
    localStorage.setItem(this.creditCardSelectedColumns, JSON.stringify(columns));
  }

  /**
   * Retrieves the selected columns for the User Access Kit page from the local storage.
   * @returns {string} The selected columns as a stringified JSON object.
   */
  getUserAccessKitSelectedColumns(): string {
    return localStorage.getItem(this.userAccessKitSelectedColumns);
  }

  /**
   * Sets the selected columns for the User Access Kit page in the local storage.
   * @param {TableComlumns[]} columns - The selected columns as a JSON object.
   */
  setUserAccessKitSelectedColumns(columns): void {
    localStorage.setItem(this.userAccessKitSelectedColumns, JSON.stringify(columns));
  }

  /**
   * Retrieves the selected columns for the Filter AD page from the local storage.
   * @returns {string} The selected columns as a stringified JSON object.
   */
  getFilterAdSelectedColumns(): string {
    return localStorage.getItem(this.filterAdSelectedColumns);
  }

  /**
   * Sets the selected columns for the Filter AD page in the local storage.
   * @param {TableComlumns[]} columns - The selected columns as a JSON object.
   */
  setFilterAdSelectedColumns(columns): void {
    localStorage.setItem(this.filterAdSelectedColumns, JSON.stringify(columns));
  }

  /**
   * Retrieves the selected columns for the Roles page from the local storage.
   * @returns {string} The selected columns as a stringified JSON object.
   */
  getRoleSelectedColumns(): string {
    return localStorage.getItem(this.roleSelectedColumns);
  }

  /**
   * Sets the selected columns for the Roles page in the local storage.
   * @param {TableComlumns[]} columns - The selected columns as a JSON object.
   */
  setRoleSelectedColumns(columns): void {
    localStorage.setItem(this.roleSelectedColumns, JSON.stringify(columns));
  }

  /**
   * Retrieves the selected columns for the GRC page from the local storage.
   * @returns {string} The selected columns as a stringified JSON object.
   */
  getGrcSelectedColumns(): string {
    return localStorage.getItem(this.grcSelectedColumns);
  }

  /**
   * Sets the selected columns for the GRC page in the local storage.
   * @param columns - The selected columns as a JSON object.
   */
  setGrcSelectedColumns(columns): void {
    localStorage.setItem(this.grcSelectedColumns, JSON.stringify(columns));
  }

  /**
   * Retrieves the current theme from the local storage.
   * @returns {string} The current theme.
   */
  getTheme(): string {
    return localStorage.getItem(this.storageThemeKey);
  }

  /**
   * Sets the current theme in the local storage.
   * @param theme - The theme to set, stored under the storageThemeKey.
   */
  setTheme(theme: string): void {
    localStorage.setItem(this.storageThemeKey, theme);
  }

  /**
   * Retrieves the user's display preferences from the local storage.
   * @returns {void}
   */
  loadUserPref() {
    const prefUser = JSON.parse(localStorage.getItem(this.userDisplayPreferences));
    if (prefUser) {
      this.userPref = prefUser;
    }
  }

  /**
   * Retrieves the value of the given preference key from the user's preferences.
   * If the key does not exist, it will return true.
   * @param key - The key to retrieve the preference for.
   * @returns {boolean} The value for the given key.
   */
  getPref(key: string): boolean {
    return typeof (this.userPref[key]) === 'boolean' ? this.userPref[key] : true;
  }

  /**
   * Sets the value of the given preference key in the user's preferences.
   * Updates the local storage with the new preferences.
   * @param key - The key for which the preference is set.
   * @param val - The boolean value to set for the given key.
   */
  setPref(key: string, val: boolean): void {
    this.userPref[key] = val;
    localStorage.setItem(this.userDisplayPreferences, JSON.stringify(this.userPref));
  }

  /**
   * Retrieves the session UUID from local storage.
   * @returns {string} The session UUID, or null if not found.
   */
  getSessionUUID(): string {
    return sessionStorage.getItem(this.sessionUUID);
  }

  /**
   * Sets the session UUID in session storage.
   * @param uuid - The session UUID to set.
   */
  setSessionUUID(uuid: string): void {
    sessionStorage.setItem(this.sessionUUID, uuid);
  }
  /**
   * Clears the session UUID from session storage.
   * This will remove the stored session identifier, effectively
   * invalidating the current session.
   */
  clearSessionUUID(): void {
    sessionStorage.removeItem(this.sessionUUID);
  }

  /**
     * Get the user region from the session storage.
     * @returns {string} The user region.
     */
  getUserRegion(): string {
    return sessionStorage.getItem(this.userRegion);
  }

  /**
   * Save the token to the session storage.
   * @param token user region to save.
   */
  setUserRegion(region: string): void {
    sessionStorage.setItem(this.userRegion, region);
  }

  /**
   * Remove the data from the session storage.
   */
  clearSessionStorageData(): void {
    sessionStorage.clear();
  }
  /**
   * Remove user region from the session storage.
   */
  clearUserRegion(): void {
    sessionStorage.removeItem(this.userRegion);;
  }

  /**
   * Retrieves the chatbot conversation history from local storage.
   * @returns {any[]} The chat history as an array of messages, or null if not found.
   */
  getChatbotHistory(): any[] {
    try {
      const history = sessionStorage.getItem(this.chatbotHistoryKey);
      return history ? JSON.parse(history) : null;
    } catch (error) {
      console.error('Error loading chatbot history:', error);
      return null;
    }
  }

  /**
   * Saves the chatbot conversation history to local storage.
   * @param history - The chat history as an array of messages to save.
   */
  setChatbotHistory(history: any[]): void {
    try {
      sessionStorage.setItem(this.chatbotHistoryKey, JSON.stringify(history));
    } catch (error) {
      console.error('Error saving chatbot history:', error);
    }
  }

  /**
   * Clears the chatbot conversation history from local storage.
   */
  clearChatbotHistory(): void {
    try {
      sessionStorage.removeItem(this.chatbotHistoryKey);
    } catch (error) {
      console.error('Error clearing chatbot history:', error);
    }
  }
}

export interface UserPref {
  seatsChart: boolean;
  usersChart: boolean;
  passwordChart: boolean;
  params: boolean;
  uploadPreloadPassword: boolean;
  preloadPassword: boolean;
  uploadPreloadUrl: boolean;
  preloadUrl: boolean;
  uploadUsers: boolean;
  users: boolean;
  roles: boolean;
  profile: boolean;
  grcDownload: boolean;
  grcList: boolean;
  grcChart: boolean;
  servers: boolean;
  ips: boolean;
  serversDoc: boolean;
  a2f: boolean;
  filterAd: boolean;
  accessKit: boolean;
  creditCards: boolean;
  uploadCreditCard: boolean;
  uploadUserAccessTemplate: boolean;
  uploadPreloadUserPassword: boolean;
  uploadApplicationAccess: boolean;
  userPreloadAccess: boolean;
  reportPreloadAccess: boolean
  deviceRestrictionDoc: boolean;
  deviceRestriction: boolean;
  uploadDeviceRestriction: boolean;
  ftpsftp: boolean,
  changeOwner: boolean,
  changeAccessKey: boolean
}
